import { useState } from "react";
import { X, EnvelopeSimple, Warning } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin modal: send a "balance due" reminder email to the client.
 *
 * Shows the current balance breakdown, optional custom note (rendered as a quoted block in
 * the email), and the most recent send timestamp. Server throttles to one email per 24h
 * per project; we surface a "Send anyway" checkbox to override.
 */
export function SendBalanceReminderModal({ open, onClose, project, onSent }) {
  const [note, setNote] = useState("");
  const [force, setForce] = useState(false);
  const [sending, setSending] = useState(false);

  if (!open || !project) return null;

  const total = Number(project.total_usd || 0);
  const paid = Number(project.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const lastSent = project.last_balance_reminder_at
    ? new Date(project.last_balance_reminder_at)
    : null;
  const hoursSince = lastSent ? (Date.now() - lastSent.getTime()) / 3600000 : null;
  const withinThrottle = hoursSince !== null && hoursSince < 24;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/admin/projects/${project.id}/send-balance-reminder`, {
        note: note.trim() || undefined,
        force: withinThrottle ? force : undefined,
      });
      toast.success(`Reminder sent to ${data.to}`);
      onSent?.(data);
      onClose();
      setNote("");
      setForce(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send reminder");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="balance-reminder-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-[#262626]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Reminder</div>
            <div className="font-display text-xl mt-1">Send balance reminder</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-px bg-[#262626] border border-[#262626]" data-testid="reminder-summary">
            <Cell label="Total" value={formatUSD(total)} />
            <Cell label="Paid" value={formatUSD(paid)} />
            <Cell label="Balance" value={formatUSD(balance)} accent />
          </div>

          <div className="text-xs text-[#a3a3a3] leading-relaxed">
            {"We'll email "}<span className="text-white">{project.client_email || project.client_name || "the client"}</span>{" a polite reminder with a one-tap pay link. They can also apply a promo code on the project page."}
          </div>

          {/* Last sent + throttle warning */}
          {lastSent && (
            <div
              className={`border p-3 text-xs flex items-start gap-2 ${
                withinThrottle ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626] bg-[#0a0a0a]"
              }`}
              data-testid="reminder-last-sent"
            >
              <Warning size={14} className={withinThrottle ? "text-[#FF2D2D] mt-0.5" : "text-[#a3a3a3] mt-0.5"} weight="fill" />
              <div className="flex-1">
                <div className={withinThrottle ? "text-[#FF2D2D]" : "text-[#a3a3a3]"}>
                  Last reminder sent {timeAgo(lastSent)}.
                </div>
                {withinThrottle && (
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={force}
                      onChange={(e) => setForce(e.target.checked)}
                      data-testid="reminder-force"
                      className="accent-[#FF2D2D]"
                    />
                    <span className="text-white">Send anyway (overrides 24h throttle)</span>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Custom note */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
              Personal message (optional, shown in email)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              data-testid="reminder-note"
              placeholder="e.g. Just a quick heads-up before we kick off the next phase…"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#FF2D2D] outline-none resize-none"
            />
            <div className="text-[10px] text-[#666] mt-1 text-right">{note.length} / 500</div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Cancel</button>
            <button
              type="submit"
              disabled={sending || balance <= 0 || (withinThrottle && !force)}
              data-testid="reminder-submit"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
            >
              <EnvelopeSimple size={12} weight="bold" />
              {sending ? "Sending…" : `Send reminder for ${formatUSD(balance)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className="bg-[#0a0a0a] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3]">{label}</div>
      <div className={`font-display text-lg mt-0.5 tabular-nums ${accent ? "text-[#FF2D2D]" : ""}`}>{value}</div>
    </div>
  );
}

function timeAgo(d) {
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}
